import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../contexts/AuthContext';
import MemberApplications from './MemberApplications';
import MemberManagement from './MemberManagement';
import AnnouncementManagement from './AnnouncementManagement';
import EventManagement from './EventManagement';
import SponsorManagement from './SponsorManagement';
import RoleManagement from './RoleManagement';
import AdjudicationDashboard from './AdjudicationDashboard';
import AuditLogViewer from './AuditLogViewer';
import PaymentsNeedingAttention from './PaymentsNeedingAttention';
import SeasonStatsDashboard from './SeasonStatsDashboard';
import WebStatsCard from './WebStatsCard';
import TestDataCleanup from './TestDataCleanup';
import PaymentRecordsList from './PaymentRecordsList';
import CompetitionAttentionPanel from './CompetitionAttentionPanel';
import { formatClubDate } from '../../utils/clubTime';
import { deriveLifecycle, type Phase } from '@shared/competitionRules';
import { getCompetitionAttentionItems, type AttentionEvent } from '@shared/competitionAttention';

const client = generateClient({ authMode: 'userPool' });

interface DashboardStats {
  pendingApplications: number;
  activeMembers: number;
  upcomingEvents: number;
}

type AdminView =
  | 'dashboard'
  | 'applications'
  | 'members'
  | 'competitions'
  | 'content'
  | 'reports'
  | 'payments'
  | 'test-data';

const SubTabs: React.FC<{
  items: { id: string; label: string }[];
  active: string;
  onChange: (id: any) => void;
}> = ({ items, active, onChange }) => (
  <div className="mb-6 border-b border-gray-200">
    <nav className="-mb-px flex space-x-6">
      {items.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`${active === t.id ? 'border-fishing-blue text-fishing-blue' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm`}
        >
          {t.label}
        </button>
      ))}
    </nav>
  </div>
);

const AdminDashboard: React.FC<{ initialAdjudicationEventId?: string }> = ({
  initialAdjudicationEventId,
}) => {
  const { user, signOut } = useAuth();
  const [currentView, setCurrentView] = useState<AdminView>(
    initialAdjudicationEventId ? 'competitions' : 'dashboard'
  );
  const [stats, setStats] = useState<DashboardStats>({
    pendingApplications: 0,
    activeMembers: 0,
    upcomingEvents: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [memberSubView, setMemberSubView] = useState<'list' | 'role-management'>('list');
  const [competitionsSubView, setCompetitionsSubView] = useState<'events' | 'adjudication'>(
    initialAdjudicationEventId ? 'adjudication' : 'events'
  );
  const [contentSubView, setContentSubView] = useState<'announcements' | 'sponsors'>(
    'announcements'
  );
  const [reportsSubView, setReportsSubView] = useState<'season-stats' | 'audit-log' | 'payments'>(
    'season-stats'
  );
  const [adjudicationEventId, setAdjudicationEventId] = useState<string>(
    initialAdjudicationEventId ?? ''
  );
  const [competitionEvents, setCompetitionEvents] = useState<AttentionEvent[]>([]);

  useEffect(() => {
    loadDashboardStats();
    loadCompetitionEvents();
  }, []);

  const loadCompetitionEvents = async () => {
    try {
      // #79-class: page fully, same fix as AdjudicationEventPicker below —
      // a filtered list() caps rows scanned, not returned.
      let data: any[] = [];
      let nextToken: string | null = null;
      do {
        const page: any = await client.models.Event.list({
          filter: { eventType: { eq: 'COMPETITION' } },
          ...(nextToken ? { nextToken } : {}),
        });
        data = data.concat(page.data || []);
        nextToken = page.nextToken ?? null;
      } while (nextToken);
      setCompetitionEvents(data);
    } catch (err) {
      console.error('Error loading competition events for the attention panel:', err);
    }
  };

  const goToAttentionItem = (eventId: string) => {
    setCurrentView('competitions');
    setCompetitionsSubView('adjudication');
    setAdjudicationEventId(eventId);
  };

  const attentionItems = getCompetitionAttentionItems(competitionEvents, new Date());

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      // BUG FIXED 9 Aug 2026: neither Member.list() call followed nextToken,
      // so both stat counts could silently undercount past a page-size
      // cutoff with no error shown — same class of bug found and fixed the
      // previous day in MemberManagement.tsx, but this file was never
      // touched then. See MemberApplications.tsx's loadApplications for the
      // identical fix and full explanation.
      const countAllPages = async (filter: Record<string, unknown>): Promise<number> => {
        let count = 0;
        let nextToken: string | null | undefined;
        do {
          const page: any = await client.models.Member.list({
            filter,
            nextToken: nextToken || undefined,
          });
          count += (page.data || []).length;
          nextToken = page.nextToken;
        } while (nextToken);
        return count;
      };

      const [pendingCount, activeCount] = await Promise.all([
        countAllPages({ status: { eq: 'PENDING' } }),
        countAllPages({ status: { eq: 'VERIFIED' } }),
      ]);
      const now = new Date().toISOString();
      // #79-class: this filtered list() read one page only, so the
      // "upcoming events" tile could silently undercount — same fault as
      // the Member.list() calls fixed above.
      let upcomingEvents: any[] = [];
      let eventsToken: string | null | undefined;
      do {
        const page: any = await client.models.Event.list({
          filter: { and: [{ isActive: { eq: true } }, { eventDate: { gt: now } }] },
          nextToken: eventsToken || undefined,
        });
        upcomingEvents = upcomingEvents.concat(page.data || []);
        eventsToken = page.nextToken;
      } while (eventsToken);
      setStats({
        pendingApplications: pendingCount,
        activeMembers: activeCount,
        upcomingEvents: upcomingEvents.length,
      });
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (e) {
      console.error('Error signing out:', e);
    }
  };

  const tabs: { id: AdminView; label: string; badge?: number }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'applications', label: 'Applications', badge: stats.pendingApplications },
    { id: 'members', label: 'Members' },
    { id: 'competitions', label: 'Competitions' },
    { id: 'content', label: 'Content' },
    { id: 'reports', label: 'Reports' },
    { id: 'payments', label: 'Payments' },
  ];
  const showTestData = import.meta.env.VITE_ENV !== 'production';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center py-4 sm:py-6 gap-2">
            <div className="flex items-center">
              <img
                src="/tumbulgum-logo.png"
                alt="Tumbulgum Fishing Club"
                className="h-8 sm:h-10 w-auto"
              />
              <h1 className="ml-2 sm:ml-3 text-lg sm:text-2xl font-bold text-gray-900">
                Admin Dashboard
              </h1>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  window.history.pushState({}, '', '/');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                className="text-xs sm:text-sm text-fishing-blue hover:underline hidden sm:inline"
              >
                ← Website
              </a>
              <span className="text-xs sm:text-sm text-gray-700 hidden sm:inline">
                {user?.email}
              </span>
              <button
                onClick={handleSignOut}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile dropdown */}
          <div className="block sm:hidden py-3">
            <label htmlFor="admin-nav-select" className="sr-only">
              Select admin section
            </label>
            <select
              id="admin-nav-select"
              value={currentView}
              onChange={(e) => setCurrentView(e.target.value as AdminView)}
              className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-fishing-blue focus:outline-none focus:ring-fishing-blue"
            >
              {tabs.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                  {t.badge ? ` (${t.badge})` : ''}
                </option>
              ))}
              {showTestData && <option value="test-data">🧹 Test Data</option>}
            </select>
          </div>
          {/* Desktop tabs */}
          <nav className="-mb-px hidden sm:flex space-x-8" aria-label="Tabs">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setCurrentView(t.id)}
                className={`${currentView === t.id ? 'border-fishing-blue text-fishing-blue' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                {t.label}
                {t.badge ? (
                  <span className="ml-2 bg-red-100 text-red-600 py-0.5 px-2 rounded-full text-xs font-medium">
                    {t.badge}
                  </span>
                ) : null}
              </button>
            ))}
            {showTestData && (
              <button
                onClick={() => setCurrentView('test-data')}
                className={`${currentView === 'test-data' ? 'border-fishing-blue text-fishing-blue' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                🧹 Test Data
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {currentView === 'dashboard' && (
          <div className="px-4 py-6 sm:px-0">
            <CompetitionAttentionPanel items={attentionItems} onSelect={goToAttentionItem} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <button
                onClick={() => setCurrentView('applications')}
                className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md hover:border-fishing-blue border border-transparent transition-all cursor-pointer text-left"
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Pending Applications
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {loading ? '...' : stats.pendingApplications}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </button>
              <button
                onClick={() => setCurrentView('members')}
                className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md hover:border-fishing-blue border border-transparent transition-all cursor-pointer text-left"
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Active Members
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {loading ? '...' : stats.activeMembers}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </button>
              <button
                onClick={() => setCurrentView('competitions')}
                className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md hover:border-fishing-blue border border-transparent transition-all cursor-pointer text-left"
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3a4 4 0 118 0v4m-4 8a2 2 0 100-4 2 2 0 000 4zm0 0v4a2 2 0 002 2h6a2 2 0 002-2v-4"
                        />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Upcoming Events
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {loading ? '...' : stats.upcomingEvents}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </button>
            </div>
            {error && (
              <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">{error}</p>
              </div>
            )}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <button
                    onClick={() => setCurrentView('applications')}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-fishing-blue bg-blue-100 hover:bg-blue-200"
                  >
                    Review Applications
                  </button>
                  <button
                    onClick={() => {
                      setCurrentView('content');
                      setContentSubView('announcements');
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-fishing-blue bg-blue-100 hover:bg-blue-200"
                  >
                    Manage Announcements
                  </button>
                  <button
                    onClick={() => {
                      setCurrentView('competitions');
                      setCompetitionsSubView('events');
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-fishing-blue bg-blue-100 hover:bg-blue-200"
                  >
                    Create Event
                  </button>
                  <button
                    onClick={() => {
                      setCurrentView('content');
                      setContentSubView('sponsors');
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-fishing-blue bg-blue-100 hover:bg-blue-200"
                  >
                    Manage Sponsors
                  </button>
                </div>
              </div>
            </div>

            {/* QR Codes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                  Registration QR Code
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Print for flyers — new member sign-ups.
                </p>
                <div className="flex flex-col items-center gap-3">
                  <div className="bg-white p-3 border-2 border-gray-200 rounded-lg" id="qr-reg">
                    <QRCodeSVG
                      value="https://www.tumbyfc.com/register"
                      size={160}
                      level="M"
                      includeMargin
                    />
                  </div>
                  <p className="text-xs text-gray-500">www.tumbyfc.com/register</p>
                  <button
                    onClick={() => {
                      const svg = document.querySelector('#qr-reg svg');
                      if (!svg) return;
                      const d = new XMLSerializer().serializeToString(svg);
                      const c = document.createElement('canvas');
                      c.width = 400;
                      c.height = 400;
                      const ctx = c.getContext('2d');
                      const img = new Image();
                      img.onload = () => {
                        ctx?.drawImage(img, 0, 0, 400, 400);
                        const a = document.createElement('a');
                        a.download = 'tumbyfc-registration-qr.png';
                        a.href = c.toDataURL('image/png');
                        a.click();
                      };
                      img.src = 'data:image/svg+xml;base64,' + btoa(d);
                    }}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    📥 Download PNG
                  </button>
                </div>
              </div>
              <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                  Competition Check-in QR Code
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Permanent QR — auto-directs to the active competition.
                </p>
                <div className="flex flex-col items-center gap-3">
                  <div className="bg-white p-3 border-2 border-gray-200 rounded-lg" id="qr-checkin">
                    <QRCodeSVG
                      value="https://www.tumbyfc.com/check-in"
                      size={160}
                      level="M"
                      includeMargin
                    />
                  </div>
                  <p className="text-xs text-gray-500">www.tumbyfc.com/check-in</p>
                  <button
                    onClick={() => {
                      const svg = document.querySelector('#qr-checkin svg');
                      if (!svg) return;
                      const d = new XMLSerializer().serializeToString(svg);
                      const c = document.createElement('canvas');
                      c.width = 400;
                      c.height = 400;
                      const ctx = c.getContext('2d');
                      const img = new Image();
                      img.onload = () => {
                        ctx?.drawImage(img, 0, 0, 400, 400);
                        const a = document.createElement('a');
                        a.download = 'tumbyfc-checkin-qr.png';
                        a.href = c.toDataURL('image/png');
                        a.click();
                      };
                      img.src = 'data:image/svg+xml;base64,' + btoa(d);
                    }}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    📥 Download PNG
                  </button>
                </div>
              </div>
              <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                  Catch Submission QR Code
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Permanent QR — auto-directs to catch submission for the active competition.
                </p>
                <div className="flex flex-col items-center gap-3">
                  <div className="bg-white p-3 border-2 border-gray-200 rounded-lg" id="qr-catch">
                    <QRCodeSVG
                      value={`${window.location.origin}/catch`}
                      size={160}
                      level="M"
                      includeMargin
                    />
                  </div>
                  <p className="text-xs text-gray-500">{`${window.location.host}/catch`}</p>
                  <button
                    onClick={() => {
                      const svg = document.querySelector('#qr-catch svg');
                      if (!svg) return;
                      const d = new XMLSerializer().serializeToString(svg);
                      const c = document.createElement('canvas');
                      c.width = 400;
                      c.height = 400;
                      const ctx = c.getContext('2d');
                      const img = new Image();
                      img.onload = () => {
                        ctx?.drawImage(img, 0, 0, 400, 400);
                        const a = document.createElement('a');
                        a.download = 'tumbyfc-catch-submission-qr.png';
                        a.href = c.toDataURL('image/png');
                        a.click();
                      };
                      img.src = 'data:image/svg+xml;base64,' + btoa(d);
                    }}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    📥 Download PNG
                  </button>
                </div>
              </div>
            </div>

            <WebStatsCard />
          </div>
        )}

        {currentView === 'applications' && (
          <div className="px-4 py-6 sm:px-0">
            <MemberApplications />
          </div>
        )}

        {currentView === 'members' && (
          <div className="px-4 py-6 sm:px-0">
            <SubTabs
              items={[
                { id: 'list', label: 'Member List' },
                { id: 'role-management', label: 'Role Management' },
              ]}
              active={memberSubView}
              onChange={setMemberSubView}
            />
            {memberSubView === 'list' && <MemberManagement />}
            {memberSubView === 'role-management' && <RoleManagement />}
          </div>
        )}

        {currentView === 'competitions' && (
          <div className="px-4 py-6 sm:px-0">
            <SubTabs
              items={[
                { id: 'events', label: 'Events' },
                { id: 'adjudication', label: 'Adjudication' },
              ]}
              active={competitionsSubView}
              onChange={setCompetitionsSubView}
            />
            {competitionsSubView === 'events' && (
              <>
                <CompetitionAttentionPanel items={attentionItems} onSelect={goToAttentionItem} />
                <EventManagement />
              </>
            )}
            {competitionsSubView === 'adjudication' &&
              (adjudicationEventId ? (
                <div>
                  {initialAdjudicationEventId && adjudicationEventId === initialAdjudicationEventId && (
                    <button
                      onClick={() => {
                        window.history.pushState(
                          {},
                          '',
                          `/admin/competition-day/${adjudicationEventId}`
                        );
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      }}
                      className="mb-4 mr-4 inline-flex items-center text-sm text-fishing-blue hover:text-blue-700"
                    >
                      <svg
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                      Back to competition
                    </button>
                  )}
                  <button
                    onClick={() => setAdjudicationEventId('')}
                    className="mb-4 inline-flex items-center text-sm text-fishing-blue hover:text-blue-700"
                  >
                    <svg
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    Back to competition list
                  </button>
                  <AdjudicationDashboard eventId={adjudicationEventId} />
                </div>
              ) : (
                <AdjudicationEventPicker onSelect={setAdjudicationEventId} />
              ))}
          </div>
        )}

        {currentView === 'content' && (
          <div className="px-4 py-6 sm:px-0">
            <SubTabs
              items={[
                { id: 'announcements', label: 'Announcements' },
                { id: 'sponsors', label: 'Sponsors' },
              ]}
              active={contentSubView}
              onChange={setContentSubView}
            />
            {contentSubView === 'announcements' && <AnnouncementManagement />}
            {contentSubView === 'sponsors' && <SponsorManagement />}
          </div>
        )}

        {currentView === 'reports' && (
          <div className="px-4 py-6 sm:px-0">
            <SubTabs
              items={[
                { id: 'season-stats', label: 'Season Stats' },
                { id: 'audit-log', label: 'Audit Log' },
                { id: 'payments', label: 'Payments needing attention' },
              ]}
              active={reportsSubView}
              onChange={setReportsSubView}
            />
            {reportsSubView === 'season-stats' && <SeasonStatsDashboard />}
            {reportsSubView === 'payments' && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Payments needing attention</h2>
                <PaymentsNeedingAttention />
              </div>
            )}
            {reportsSubView === 'audit-log' && (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Admin Audit Log</h2>
                <AuditLogViewer />
              </>
            )}
          </div>
        )}

        {currentView === 'test-data' && showTestData && (
          <div className="px-4 sm:px-0">
            <TestDataCleanup />
          </div>
        )}

        {currentView === 'payments' && (
          <div className="px-4 py-6 sm:px-0">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Payment Records</h2>
            <PaymentRecordsList />
          </div>
        )}
      </main>
    </div>
  );
};

const AdjudicationEventPicker: React.FC<{ onSelect: (eventId: string) => void }> = ({
  onSelect,
}) => {
  const [competitions, setCompetitions] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      try {
        // #79-class: page fully — a filtered list() caps rows SCANNED, not
        // returned, so a single call could silently drop a competition from
        // the adjudication picker.
        let data: any[] = [];
        let nextToken: string | null = null;
        do {
          const page: any = await client.models.Event.list({
            filter: { eventType: { eq: 'COMPETITION' }, isActive: { eq: true } },
            ...(nextToken ? { nextToken } : {}),
          });
          data = data.concat(page.data || []);
          nextToken = page.nextToken ?? null;
        } while (nextToken);
        setCompetitions(
          data.sort(
            (a: any, b: any) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime()
          )
        );
      } catch (err) {
        console.error('Error loading competitions:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="text-gray-500 py-8 text-center">Loading competitions…</div>;

  const phaseLabel: Record<Phase, string> = {
    CANCELLED: 'Cancelled',
    PUBLISHED: 'Published',
    ADJUDICATED: 'Adjudicated',
    TIMING_INCOMPLETE: 'Timing incomplete',
    ENDED: 'Ended',
    FISHING: 'Fishing',
    SIGN_ON: 'Sign-on open',
    SCHEDULED: 'Scheduled',
  };

  const phaseColours: Record<Phase, string> = {
    CANCELLED: 'bg-red-100 text-red-800',
    PUBLISHED: 'bg-purple-100 text-purple-800',
    ADJUDICATED: 'bg-blue-100 text-blue-800',
    TIMING_INCOMPLETE: 'bg-red-100 text-red-800',
    ENDED: 'bg-yellow-100 text-yellow-800',
    FISHING: 'bg-green-100 text-green-800',
    SIGN_ON: 'bg-teal-100 text-teal-800',
    SCHEDULED: 'bg-gray-100 text-gray-700',
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Competition Adjudication</h2>
      {competitions.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
          No competitions found. Create a competition event first.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {competitions.map((comp: any) => {
            const phase = deriveLifecycle(comp, new Date()).phase;
            return (
              <button
                key={comp.id}
                onClick={() => onSelect(comp.id)}
                className="bg-white shadow rounded-lg p-5 text-left hover:shadow-md transition-shadow border border-transparent hover:border-fishing-blue"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 text-sm">{comp.title}</h3>
                  <span
                    className={`ml-2 flex-shrink-0 inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${phaseColours[phase]}`}
                  >
                    {phaseLabel[phase]}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {formatClubDate(comp.eventDate, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
